<template>
    <div>
        <!--
            Replaced by Skeleton loader.
            <FullPageLoader :isLoading="loading"/>
        -->

        <!-- Escalate invoice/contract modals -->
        <InvoiceEscalateModals
            ref="InvoiceEscalateModals"
            :showEscalation="showEscalation"
            :showOutEscalation="showOutEscalation"
            @escalated="loadInvoice"
        />

        <!-- Add payment modal -->
        <b-modal :active.sync="showAddPaymentModal" id="addpaymentmodal" aria-modal :width="800" scroll="keep">
            <header class="modal-card-head">
                <p class="modal-card-title">{{ type == 'credit' ? 'Terugstorting' : 'Betaling' }} toevoegen</p>
            </header>
            <section class="modal-card-body" style="border-radius:0;">
                <b-field label="Betaalmethode"
                    :message="getError(errors.addpayment, 'method')"
                    :type="getType(errors.addpayment, 'method', '')"
                >
                    <b-select placeholder="Betaalmethode" v-model="addpayment.method">
                        <option :value="null">-- Maak een keuze --</option>
                        <option value="manual">Handmatige betaling</option>
                        <option value="incasso" :disabled="type == 'credit' || !user['organisation_incasso'] ||!!user.deurwaarder" v-show="type != 'credit'">Automatische incasso{{ $store.state.env == 'demo' ? ' (uitgeschakeld in demo-omgeving)' : '' }}</option>
                    </b-select>
                </b-field>

                <p v-if="addpayment.method == 'incasso'">
                    <strong>Let op:</strong>&nbsp;indien er al een incasso opdracht open staat wordt de openstaande opdracht geannuleerd. Het is enkel mogelijk om een incasso opdracht aan te maken voor het volledige openstaande bedrag.
                </p>

                <b-field label="Betaal datum"
                    :message="getError(errors.addpayment, 'date')"
                    :type="getType(errors.addpayment, 'date', '')"
                >
                    <b-datepicker
                        :nearby-selectable-month-days="true"
                        icon="calendar-alt"
                        icon-pack="fas"
                        :disabled="_.isNull(addpayment.method)"
                        :min-date="addpayment_minDate"
                        :max-date="addpayment_maxDate"
                        v-model="addpayment.date"
                    />
                </b-field>

                <b-field label="Bedrag"
                    :message="getError(errors.addpayment, 'price') ? getError(errors.addpayment, 'price') : `Maximale bedrag: € ${parseFloat(maxPrice).toFixed(2)}`"
                    :type="getType(errors.addpayment, 'price', '') ? getType(errors.addpayment, 'price', '') : ''"
                >
                    <b-input type="text" :disabled="_.isNull(addpayment.method) || addpayment.method == 'incasso'" v-model="addpayment.price" icon="euro-sign" expanded></b-input>
                </b-field>
            </section>
            <footer class="modal-card-foot">
                <b-button type="is-light" @click="showAddPaymentModal = false" :disabled="loading">Annuleren</b-button>
                <b-button type="is-success" @click="addPayment" :loading="loading" :disabled="loading || _.isNull(addpayment.method) || !addpayment.price || parseFloat(addpayment.price.replace(',', '.')) <= 0">Toevoegen</b-button>
            </footer>
        </b-modal>

        <!-- Reminder modal -->
        <b-modal :active.sync="reminderModalOpen" class="invitemodal" :width="640" scroll="keep">
            <div class="modal-card">
                <header class="modal-card-head"><p class="modal-card-title">Verstuur een <span v-if="record.status == 'reminded_1'">2e&nbsp;</span>betaalherinnering e-mail</p></header>
                <section class="modal-card-body" style="overflow: hidden;border-radius:0;">
                    <p><strong>Je bericht</strong></p>
                    <QuillEditor
                        :theContent="reminderRecord.content"
                        :disabled="reminding"
                        @contentChanged="reminderContentChanged"
                        :toolbar="[
                            ['bold', 'italic', 'underline', 'strike'],
                            ['blockquote'],
                            [{ 'list': 'ordered' }, { 'list': 'bullet' }],
                            [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
                            [{ 'color': [] }],
                        ]"
                    />
                </section>
                <footer class="modal-card-foot">
                    <b-button type="is-light" @click="reminderModalOpen = false, reminding = false;">Annuleren</b-button>
                    <b-button type="is-success" @click="sendReminder" :loading="reminding">Versturen</b-button>
                </footer>
            </div>
        </b-modal>

        <div class="row">
            <div class="column medium-3 text-right float-right">
                <b-tooltip :label="hideSensitiveData ? 'Toon gevoelige data' : 'Verberg gevoelige data'" position="is-left">
                    <b-button :type="hideSensitiveData ? 'is-success' : 'is-danger'" :icon-right="hideSensitiveData ? 'eye' : 'eye-slash'" @click="$store.dispatch('toggleSensitiveData')"></b-button>
                </b-tooltip>
                <b-button tag="router-link" :to="`/panel/facturen/${type}`" type="is-dark">Terug naar overzicht</b-button>
            </div>
            <div class="column medium-9">
                <h1>{{ type == 'credit' ? 'Creditfactuur' : 'Factuur' }}&nbsp;{{ record.number }}</h1>
            </div>
        </div>

        <div class="row">
            <div class="column">
                <form class="form" :class="{
                    'gray-top': loading,
                    'red-top': !loading && (type == 'credit' || (record && !_.isNull(record?.escalated_at))),
                    'green-top': !loading && (type == 'recent' || type == 'ingepland' || type == 'los') && (record && _.isNull(record?.escalated_at))
                }">
                    <b-tabs size="is-medium">
                        <b-tab-item label="Algemeen">
                            <table class="table is-fullwidth is-striped">
                                <tbody>
                                    <tr v-if="user && user.organisation_locations && user.organisation_locations.length > 1 && record?.organisation_location">
                                        <td width="200"><strong>Vestiging</strong></td>
                                        <td>
                                            <b-skeleton v-if="loading" width="150px" :animated="true"></b-skeleton>
                                            <template v-else>{{ record.organisation_location.name }} ({{ record.organisation_location.city }})</template>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td width="200"><strong>{{ type == 'credit' ? 'Aangemaakt op' : 'Factuur datum' }}</strong></td>
                                        <td>
                                            <b-skeleton v-if="loading" width="120px" :animated="true"></b-skeleton>
                                            <template v-else>{{ moment(record.invoice_date).format('DD-MM-YYYY') }}</template>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td width="200"><strong>Factuur verstuurd</strong></td>
                                        <td>
                                            <b-skeleton v-if="loading" width="80px" :animated="true"></b-skeleton>
                                            <template v-else>{{ record.send_at ? moment(record.send_at).format('DD-MM-YYYY') : 'Nee' }}</template>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td width="200"><strong>Factuur PDF</strong></td>
                                        <td>
                                            <b-skeleton v-if="loading" width="100px" :animated="true"></b-skeleton>
                                            <template v-else>
                                                <a :href="'/uploads'+record.pdf" target="_blank" v-if="record.pdf">Download PDF</a>
                                                <span v-else>-</span>
                                            </template>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td width="200"><strong>Status</strong></td>
                                        <td>
                                            <b-skeleton v-if="loading" width="150px" :animated="true"></b-skeleton>
                                            <template v-else>
                                                <!-- <span v-if="record.status == 'paid' && type == 'credit'">Betaald</span> -->
                                                <span>{{ invoiceStatus(record.status) }}</span>
                                                <span v-if="record.invoice_payments && _.has(record, 'invoice_payments') && record.invoice_payments.length && record.invoice_payments[0].type == 'manual' && _.isNull(record.paid) && record.status != 'reminder_2' && !_.isNull(record.send_at) && type != 'credit' && record.status != 'incasso_planned'">&nbsp;-&nbsp;<a @click="reminderModalOpen = true">Verstuur herinnering</a></span>
                                                <EscalationOptionsDropdown
                                                    :id="record.id"
                                                    :status="record.status"
                                                    @reload="loadInvoice"
                                                    v-if="record && record.status"
                                                />
                                            </template>
                                        </td>
                                    </tr>
                                    <tr v-if="record && record.contract">
                                        <td width="200"><strong>Contract</strong></td>
                                        <td>
                                            <b-skeleton v-if="loading" width="80px" :animated="true"></b-skeleton>
                                            <template v-else>
                                                <router-link :to="{ name: 'ContractsRecordView', params: { id: record.contract.id } }">{{ record.contract.id }}</router-link>
                                            </template>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td width="200"><strong>Klant</strong></td>
                                        <td>
                                            <b-skeleton v-if="loading" width="200px" :animated="true"></b-skeleton>
                                            <template v-else>
                                                <router-link v-if="record.customer" :to="{ name: 'CustomersRecord', params: { id: record.customer.id } }">{{ hideSensitiveData ? '*********' : `${record.customer.firstname} ${record.customer.lastname} `}}</router-link>
                                                <span v-else>-</span>
                                            </template>
                                        </td>
                                    </tr>
                                    <tr v-if="type == 'credit' && _.has(record, 'credit_of.id')">
                                        <td width="200"><strong>Bijbehorende factuur</strong></td>
                                        <td>
                                            <router-link :to="{ name: 'InvoicesRecord', params: {type: 'recent', id: record.credit_of.id} }" type="is-primary">Factuur {{ hideSensitiveData ? '*********' : record.credit_of.id }}</router-link>
                                        </td>
                                    </tr>
                                    <tr v-if="showEscalation && record">
                                        <td width="200"><strong>Geëscaleerd</strong></td>
                                        <td style="display: flex;align-items: center;">
                                            <span v-if="record.escalated_at">
                                                {{ moment(record.escalated_at).format('DD-MM-YYYY') }}
                                            </span>
                                            <span v-else>{{ record ? 'Nee' : '-' }}</span>
                                            &nbsp;
                                            <b-button type="is-warning is-light is-small" v-if="_.isNull(record.escalated_at)" :disabled="!record" @click="$refs['InvoiceEscalateModals'].toggleEscalateModal({esc_contract: _.has(record, 'contract.id') ? record.contract.id : null })">
                                                {{ escalationName == 'escalatie' ? 'Escaleren' : 'Deurwaarder' }}
                                            </b-button>
                                        </td>
                                    </tr>
                                    <tr v-if="showEscalation && record && record.escalation">
                                        <td width="200"><strong>Escalatie dossier</strong></td>
                                        <td>
                                            <router-link :to="{ name: 'EscalationRecord', params: {id: record.escalation.id} }">Dossier {{ record.escalation.number }}</router-link>
                                        </td>
                                    </tr>
                                    <tr v-if="showEscalation && record && record.escalation">
                                        <td width="200"><strong>Escalatiepakket</strong></td>
                                        <td>
                                            <a :href="`/export/escalation/${record.escalation.id}/${record.escalation.uuid}?user=${user.id}`" v-if="record.escalation.uuid" target="_blank">Download</a>
                                            <span v-else>-</span>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </b-tab-item>

                        <b-tab-item label="Contract facturen" v-if="record && !_.isNull(record.contract)">
                            <InvoicesTable
                                type="contract"
                                ref="contractinvoices"
                                :id="record.contract.id"
                                v-if="record && _.has(record, 'contract.id')"
                            />
                        </b-tab-item>
                        <b-tab-item label="Klant facturen" v-if="record && _.isNull(record.contract)">
                            <InvoicesTable
                                type="customer"
                                :id="record.customer.id"
                            />
                        </b-tab-item>
                    </b-tabs>
                </form>
            </div>
        </div>

        <!-- Factuur / Producten -->
        <div class="row">
            <div class="column">
                <form class="form verticalpadding">
                    <h3>Product<span v-show="record.invoice_products && record.invoice_products.length > 1">en</span></h3>

                    <!-- Products skeleton -->
                    <div v-if="loading">
                        <b-skeleton height="50px" :animated="true"></b-skeleton>
                        <b-skeleton height="50px" :animated="true"></b-skeleton>
                        <b-skeleton height="50px" :animated="true"></b-skeleton>
                    </div>

                    <!-- Actual products when loaded -->
                    <ProductsSummary
                        v-else
                        :products="record.invoice_products"
                        :showTotals="true"
                        :type="record?.type"
                        :record="record"
                        :editingProducts="editingProducts"
                    />
                    <hr>
                </form>
            </div>
        </div>

        <!-- Betalingen -->
        <div class="row">
            <div class="column">
                <form class="form verticalpadding">
                    <h3>{{ type == 'credit' ? 'Terugstortingen' : 'Betalingen' }}</h3>

                    <!-- Payments skeleton -->
                    <div v-if="loading">
                        <b-skeleton height="40px" :animated="true"></b-skeleton>
                        <b-skeleton height="40px" :animated="true"></b-skeleton>
                        <b-skeleton height="40px" :animated="true"></b-skeleton>
                    </div>

                    <!-- Actual payments when loaded -->
                    <InvoicePaymentsTable
                        v-show="!loading"
                        :records="record.invoice_payments"
                        @loadInvoice="loadInvoice"
                    />

                    <div v-if="record.invoice_payments && record.invoice_payments.length">
                        <div class="row">
                            <div class="column text-right">
                                <!-- Dummy button to show tooltip -->
                                <b-tooltip :label="cantAddPaymentInfo" position="is-left" v-if="cantAddPaymentInfo">
                                    <b-button disabled type="is-primary">
                                        {{ type == 'credit' ? 'Terugstorting toevoegen' : 'Betaling aanmaken' }}
                                    </b-button>
                                </b-tooltip>
                                <!-- Real button to show tooltip -->
                                <b-button :disabled="loading" type="is-primary" @click="showAddPaymentModal = true" v-else>
                                    {{ type == 'credit' ? 'Terugstorting toevoegen' : 'Betaling aanmaken' }}
                                </b-button>
                            </div>
                        </div>
                    </div>
                </form>
            </div>
        </div>

        <!-- Creditfacturen -->
        <div class="row" v-if="record.type != 'credit' && type != 'credit' && !user.deurwaarder">
            <div class="column">
                <form class="form verticalpadding">
                    <h3>Creditfactuur</h3>

                    <!-- Credit invoices skeleton -->
                    <div v-if="loading">
                        <b-skeleton height="40px" :animated="true"></b-skeleton>
                        <b-skeleton height="40px" :animated="true"></b-skeleton>
                    </div>

                    <div v-else-if="record.credits && record.credits.length">
                        <b-table
                            :data="record.credits"
                            :striped="true"
                            :loading="loading"
                            :mobile-cards="true"
                            class="table-with-dropdown"
                        >
                            <b-table-column v-slot="props" field="number" label="Factuur nr." width="160">
                                {{ props.row.number }}
                            </b-table-column>

                            <b-table-column v-slot="props" field="status" label="Status" width="150" sortable>
                                <b-tag :type="_.isNull(props.row.status) ? 'is-dark' : props.row.status == 'paid' ? 'is-success' : _.includes(['multiple_fails', 'storno'], props.row.status) ? 'is-danger' : _.includes(['one_fail'], props.row.status) ? 'is-warning' : _.includes(['cancelled', 'reminder_1', 'reminder_2'], props.row.status) ? 'is-warning' : 'is-info'">{{ invoiceStatus(props.row.status) }}</b-tag>
                            </b-table-column>

                            <b-table-column v-slot="props" field="send_at" label="Verstuurd" width="180" sortable>
                                <span v-if="props.row.send_at">
                                    {{ props.row.send_at|humanDate }}
                                </span>
                                <span v-else>Nee</span>
                            </b-table-column>

                            <b-table-column v-slot="props" field="total_price" label="Totaal" numeric>
                                <span v-if="hideSensitiveData">&euro;&nbsp;********</span>
                                <span v-else-if="props.row.total" class="text-danger">-&nbsp;€&nbsp;{{ props.row.total }}</span>
                                <span v-else>-</span>
                            </b-table-column>

                            <b-table-column v-slot="props" numeric width="150" class="text-right">
                                <InvoiceOptionsDropdown
                                    type="credit"
                                    :record="props.row"
                                    @reloadInvoices="loadInvoice"
                                />
                            </b-table-column>
                        </b-table>
                        <div class="text-right">
                            <br>
                            <CreateCreditInvoiceButton
                                :deposit_products="record.deposit_products"
                                :products="record.invoice_products"
                                :customerId="record.customer.id"
                                :number="record.number"
                                :contractId="record.contract?.id"
                                :invoiceId="record.id"
                                :endDate="record.contract?.end_date"
                                :hasCredits="true"
                            />
                        </div>
                    </div>
                    <div v-else-if="!loading" class="text-center">
                        <p>Geen creditfactuur gevonden.</p>
                        <CreateCreditInvoiceButton
                            :deposit_products="record.deposit_products"
                            :products="record.invoice_products"
                            :customerId="record.customer.id"
                            :number="record.number"
                            :contractId="record.contract?.id"
                            :invoiceId="record.id"
                            :endDate="record.contract?.end_date"
                        />
                    </div>
                </form>
            </div>
        </div>

        <!-- Opmerkingen -->
        <CommunicationLogs
            type="invoice"
            :logs="record.invoice_logs"
            :loading="loading"
            :customer="record.customer"
            :invoice="record.id"
            :contract="record.contract?.id"
            @loadInvoice="loadInvoice"
        />
    </div>
</template>

<script>
import InvoicePaymentsTable from './InvoicePaymentsTable'
// import LogsRecord from '@/vue/logs/LogsRecord'
import formvalidationMixin from '@/mixins/formvalidationMixin.js'
import toastMixin from '@/mixins/toastMixins.js'
import filterMixins from '@/mixins/filterMixins.js'
import escalationMixin from '@/mixins/escalationMixin.js'
import InvoiceLog from './components/InvoiceLog'
import QuillEditor from '../QuillEditor'
import InvoicesTable from '@/vue/invoices/components/InvoicesTable'
import CreateCreditInvoiceButton from '@/vue/creditinvoice/CreateCreditInvoiceButton'
import InvoiceStatusMixin from '@/mixins/invoiceStatusMixin.js'
import EscalationOptionsDropdown from './components/EscalationOptionsDropdown.vue'
import InvoiceEscalateModals from '@/vue/invoices/components/InvoiceEscalateModals'
import CommunicationLogs from '@/vue/CommunicationLogs.vue'
import ProductsSummary from '@/vue/products/components/ProductsSummary.vue'
import InvoiceOptionsDropdown from './components/InvoiceOptionsDropdown.vue'

export default {
    name: 'InvoicesRecord',

    components: {
        InvoicePaymentsTable,
        // LogsRecord,
        InvoiceLog,
        QuillEditor,
        InvoicesTable,
        CreateCreditInvoiceButton,
        EscalationOptionsDropdown,
        InvoiceEscalateModals,
        CommunicationLogs,
        ProductsSummary,
        InvoiceOptionsDropdown
    },

    mixins: [formvalidationMixin, toastMixin, filterMixins, InvoiceStatusMixin, escalationMixin],

    data() {
        return {
            loading: false,
            type: this.$route.params.type,
            record: {},
            errors: {
                addpayment: {}
            },

            editingProducts: false,

            showAddPaymentModal: false,
            addpayment: {
                method: null,
                date: new Date(),
                price: 0.00
            },
            manualPaymentPossible: false,

            // Reminder
            reminderModalOpen: false,
            reminding: false,
            reminderRecord: {}
        }
    },

    computed: {
        send() {
            return !!this.record.send_at
        },

        addpayment_minDate(){
            return this.addpayment.method == 'incasso' ? moment(new Date()).endOf('day').toDate() : moment(new Date()).endOf('day').add(-6, 'month').toDate()
        },

        addpayment_maxDate(){
            return this.addpayment.method == 'incasso' ? moment(new Date()).endOf('day').add(1, 'years').toDate() : moment(new Date()).endOf('day').toDate()
        },

        user() {
            return this.$store.state.user
        },

        hideSensitiveData() {
            return this.$store.state.hideSensitiveData
        },

        escalationName() {
            return _.has(this.user, 'organisation_settings.escalation_name') ? this.user.organisation_settings.escalation_name : 'escalatie'
        },

        maxPrice() {
            if (!this.record.invoice_payments) return 0;

            const totalPaid = this.record.invoice_payments.reduce((sum, payment) => {
                return sum + parseFloat(payment.paid || 0)
            }, 0);

            const total = parseFloat(this.record.total || 0)
            const remaining = total - totalPaid

            return remaining.toFixed(2)
        },

        // Depricated function
        // canAddPayment() {
        //     const hasPlannedDirectDebit = this.record.invoice_payments?.some(payment =>
        //         ['incasso_planned_bank', 'incasso_planned'].includes(payment.status)
        //     )

        //     return !hasPlannedDirectDebit && parseFloat(this.maxPrice) > 0 && this.manualPaymentPossible
        // },

        cantAddPaymentInfo() {
            const hasPlannedDirectDebit = this.record.invoice_payments?.some(payment =>
                ['incasso_planned_bank', 'incasso_planned'].includes(payment.status)
            );

            if (hasPlannedDirectDebit) {
                return 'Er is een incasso aangemeld. Wacht op de terugkoppeling voordat je een nieuwe betaling aanmaakt.'
            }

            if (parseFloat(this.maxPrice) <= 0) {
                return 'Er is geen openstaand bedrag meer'
            }

            return null;
        }
    },

    created () {
        this.loadInvoice()
    },

    methods: {
        loadInvoice(){
            this.loading = true

            axios.get('/api/invoices/'+this.$route.params.id+'?logs=true', {
                headers: {'Authorization': 'bearer ' + this.$store.state.authToken}
            }).then((response) => {
                this.record = response.data

                // Set if manual payment is acceptable
                _.each(response.data.invoice_payments, (record) => {
                    if(record.type == 'incasso' && (record.status == 'planned' || record.status == 'open' || record.status == 'storno' || record.status == 'cancelled')){
                        this.manualPaymentPossible = true
                    }
                    if((record.type == 'manual') && (record.status == 'open' || record.status == 'open_due') && !_.isNull(this.record.send_at)){
                        this.manualPaymentPossible = true
                    }
                    if (record.in_out == 'out'){
                        this.manualPaymentPossible = true
                    }
                })

                this.loading = false

                if(_.includes([null, 'open_due', 'reminder_1', 'partly_paid'], response.data.status) && _.has(response.data, 'invoice_payments') && response.data.invoice_payments.length && response.data.invoice_payments[0].type == 'manual'){
                    this.fetchReminderMail()
                }
            }).catch((error) => {
                this.showError('Error: Factuur kan niet geopend worden!')
                // console.error(error)
                this.$router.push({name: 'InvoiceOverview'})
            })
        },

        addPayment(){
            this.loading = true

            this.errors = {}
            this.addpayment.type = this.record.type
            this.addpayment.date = moment(new Date(this.addpayment.date)).startOf('day')._d

            // Validate and convert price to proper float format
            let price = this.addpayment.price;
            if (typeof price === 'string') {
                // Replace comma with dot and validate
                price = price.replace(',', '.');
                const priceFloat = parseFloat(price);

                if (isNaN(priceFloat) || priceFloat <= 0) {
                    this.showError('Voer een geldig bedrag in.');
                    this.loading = false;
                    return;
                }

                if (priceFloat > parseFloat(this.maxPrice)) {
                    this.showError('Het ingevoerde bedrag is hoger dan het openstaande bedrag.');
                    this.loading = false;
                    return;
                }

                // Convert to proper float format for backend
                this.addpayment.price = priceFloat.toFixed(2);
            }

            axios.post('/api/invoices/'+this.$route.params.id+'/payment', {...this.addpayment}, {
                headers: {'Authorization': 'bearer ' + this.$store.state.authToken}
            }).then(() => {
                this.showMessage('Betaling toegevoegd!')
                this.showAddPaymentModal = false

                this.loadInvoice()
                this.$store.dispatch('fetchInvoicesNeedsAction')

                if (this.$refs['contractinvoices']) {
                    this.$refs['contractinvoices'].loadAsyncData() // refresh 'Contract facturen' so the payment status is correct.s
                }
            }).catch((error) => {
                console.error('[❌] Error while validating the payment:', error)
                this.errors.addpayment = {...error.response.data.errors}

                if(error.response.data == 'price-error'){
                    return this.showError('Ingevulde bedrag is te hoog!')
                }

                this.showError('Opslaan mislukt - Onvoldoende of onjuiste gegevens.')
            }).then(() => {
                this.loading = false
            })
        },

        fetchReminderMail(){
            axios.get('/api/communication/reminder_mail?organisation='+this.record.organisation, {
                headers: {'Authorization': 'bearer ' + this.$store.state.authToken}
            }).then((response) => {
                // Replace the {nummer} and {datum}.
                response.data.content = response.data.content.replace(new RegExp('{nummer}', 'g'), this.record.number)
                response.data.content = response.data.content.replace(new RegExp('{datum}', 'g'), moment(new Date(this.record.invoice_date)).format('DD-MM-YYYY'))

                this.reminderRecord = response.data
            }).catch((e) => {
                console.error(e)
            })
        },

        reminderContentChanged(content){
            this.reminderRecord.content = content
        },

        sendReminder(){
            this.reminding = true

            axios.post('/api/invoices/'+this.$route.params.id+'/reminder', {content: this.reminderRecord.content}, {
                headers: {'Authorization': 'bearer ' + this.$store.state.authToken}
            }).then(() => {
                this.showMessage('Herinnering verstuurd!')
                this.reminderModalOpen = false
                this.loadInvoice()
                this.$store.dispatch('fetchInvoicesNeedsAction')
            }).catch((e) => {
                console.error(e)
                this.showError('Herinnering versturen mislukt! Probeer het opnieuw.')
            }).then(() => {
                this.reminding = false
            })
        }
    },

    watch: {
        showAddPaymentModal(newVal) {
            if (newVal) {
                this.addpayment.price = this.maxPrice;

                if (parseFloat(this.maxPrice) <= 0) {
                    this.showError('Betaling aanmaken is niet mogelijk. Er is geen openstaand bedrag meer.');
                    this.showAddPaymentModal = false;
                }
            } else {
                this.addpayment.method = null;
            }
        },

        'addpayment.method': function(newVal){
            this.addpayment.date = newVal == 'incasso' ? this.addpayment_minDate : new Date()

            if (newVal == 'incasso') {
                this.addpayment.price = this.maxPrice;
            }
        },

        'addpayment.price': function(newVal) {
            if (typeof newVal === 'string') {
                // Allow free text input, but replace comma with dot for consistency
                let price = newVal.replace(',', '.');

                // Only allow digits, dots, and empty string
                if (price === '' || /^\d*\.?\d*$/.test(price)) {
                    this.addpayment.price = price;
                }
            }
        }
    }
}
</script>

<style scoped>
/* Right align the dropdown column */
.text-right {
    text-align: right !important;
}
</style>
